/**
 * Les refus TRANSVERSES du serveur, dits dans la langue de l'écran — une table,
 * une seule, pour tout le produit (décision PO du 2026-09-24).
 *
 * ## Le défaut réparé ici
 *
 * 🔴 Mesuré le 2026-09-23 sur les écrans de commissions : un droit manquant
 * affichait « Permission 'commissions:manage' required for this action », et une
 * saisie hors bornes « Input should be less than or equal to 100 » — en ANGLAIS
 * dans l'interface française. Ces refus ne sont propres à aucun module : ils
 * sortent des gardes partagées (`_require_admin_permission`, `requires_feature`,
 * la garde d'entité) et du gestionnaire global des 422 Pydantic. Chaque table de
 * module (commissions, tableaux, appels d'offres) les laissait donc passer en
 * phrase du serveur, et les traduire dans l'une les aurait laissés crus dans les
 * autres — ou traduits autrement.
 *
 * ## Comment on s'en sert : EN REPLI
 *
 * Chaque fonction « message… » d'un module garde SA table d'abord (elle connaît
 * le geste, elle dit la sortie propre à l'écran), puis consulte celle-ci, puis
 * seulement la phrase du serveur. Un gardien
 * (`toutRefusDeModuleConsulteLaTableTransverse.test.ts`) exige ce repli de
 * chacune.
 *
 * ## Les 422 Pydantic : TOUTES les erreurs, par leur type
 *
 * Dites par `api-error-transverse.saisie.ts`, ré-exporté ici : `type` et `ctx` (la
 * borne) suffisent à écrire la phrase dans la langue de l'écran.
 *
 * ⚠️ Lecture par FORME, jamais `instanceof ApiError` : les appels d'offres lèvent
 * leurs propres erreurs (`problem`, `status`), et plusieurs cahiers de test
 * simulent `@/lib/api-error` sans sa classe — un `instanceof` y lèverait.
 *
 * ## Les INCIDENTS : décidés par le STATUT, pas par le code
 *
 * Une vraie panne sort par les gestionnaires GLOBAUX du serveur
 * (`core/exception_handlers.py`) : 500 `INTERNAL_ERROR` « Erreur interne du
 * serveur. », 500 `DATABASE_ERROR` / `MISSING_TABLE` / `MISSING_COLUMN`, 503
 * `SERVICE_BUSY` — ou par un proxy (502, 504, corps illisible). Aucune table ne
 * les connaissait : la phrase FRANÇAISE du serveur atteignait l'écran anglais
 * (revue de la vague 11, TRANSFERT/dette/0). Tout 5xx dit donc l'incident, dans
 * la langue de l'écran, et GARDE la référence de la requête quand le serveur la
 * donne : c'est elle que le support demande (`statutDuRefus`, `phraseDIncident`,
 * lus aussi par `toast-error.ts` — une seule écriture de la règle).
 *
 * Une COUPURE RÉSEAU n'est PAS un incident : la requête n'a jamais atteint le
 * serveur, elle n'a donc aucune référence, et le geste est du côté de
 * l'utilisateur. Elle se dit par `common.networkError`, la phrase que le produit
 * emploie déjà partout (`echecReseau.ts`), APPELÉE SANS `defaultValue` : la clé
 * existe au catalogue, un second texte serait inerte.
 *
 * ⚠️ Les clés sont écrites EN ENTIER dans l'appel à `t` : le gardien
 * `i18n-keys-exist` ne lit que les appels.
 */

import type { TFunction } from 'i18next'

import { direLesEntrees, phraseDEntreeDeValidation, type EntreeDeValidation } from './api-error-transverse.saisie'

export { direLesEntrees, phraseDEntreeDeValidation, type EntreeDeValidation } from './api-error-transverse.saisie'

/**
 * Le refus d'une requête SANS fichier de plus de 32 Mo : 413 de la borne AMONT du corps
 * (`core/borne_de_corps.py`, décision PO du 2026-09-25), rendu AVANT le routage — aucune
 * table de module ne peut donc le connaître. Le serveur joint le plafond (`maximum_mo`).
 */
export const REFUS_SAISIE_TROP_LOURDE = 'REQUEST_PAYLOAD_TOO_LARGE'

function objet(valeur: unknown): Record<string, unknown> | null {
    return valeur && typeof valeur === 'object' && !Array.isArray(valeur)
        ? (valeur as Record<string, unknown>)
        : null
}

/** Le statut HTTP : `ApiError.statusCode`, `status` d'une enveloppe, `problem.status`, ou
 *  `httpStatus` des lecteurs de module (contrats cadres, référentiels d'AO…). */
export function statutDuRefus(erreur: unknown): number | null {
    const e = objet(erreur)
    const statut = e?.statusCode ?? e?.status ?? e?.httpStatus ?? objet(e?.problem)?.status
    return typeof statut === 'number' ? statut : null
}

/** Un INCIDENT : le serveur n'a pas REFUSÉ, il n'a pas PU (tout 5xx). */
export function estUnIncident(erreur: unknown): boolean {
    const statut = statutDuRefus(erreur)
    return statut !== null && statut >= 500
}

/**
 * La phrase d'un incident — seule écriture. Avec la RÉFÉRENCE de la requête quand
 * l'appelant l'a (sinon, un toast la porte dans sa description, avec « Copier »).
 */
export function phraseDIncident(t: TFunction, reference: string | null = null): string {
    return reference
        ? t('common.refusTransverses.incidentAvecCode', {
              code: reference,
              defaultValue:
                  'Un incident technique a empêché l’opération. Réessayez dans un instant ; si le problème persiste, contactez le support en lui transmettant ce code : {{code}}.',
          })
        : t(
              'common.refusTransverses.incident',
              'Un incident technique a empêché l’opération. Réessayez dans un instant ; si le problème persiste, contactez le support.',
          )
}

/**
 * La référence de la requête, lue par FORME : `ApiError.requestId`, ou le corps
 * (`problem`) d'une erreur des appels d'offres — `detail.request_id` ou `request_id`.
 * Sans la seconde lecture, un 500 levé par `handleTenderError` perdait son code
 * support dans chaque toast du module (FICHE/dette/3).
 */
function referenceDe(erreur: unknown): string | null {
    const e = objet(erreur)
    const corps = objet(e?.problem)
    const reference = e?.requestId ?? objet(corps?.detail)?.request_id ?? corps?.request_id
    return typeof reference === 'string' && reference ? reference : null
}

/** Le code du refus : `ApiError.code`, `code` d'une erreur de module, ou `problem.detail.code`. */
export function codeDuRefusTransverse(erreur: unknown): string | null {
    const e = objet(erreur)
    const code = e?.code ?? objet(objet(e?.problem)?.detail)?.code
    return typeof code === 'string' && code !== 'UNKNOWN' ? code : null
}

/** Les erreurs Pydantic du refus : `ApiError.details.validation_errors`, ou `problem.detail`. */
export function entreesDeValidation(erreur: unknown): EntreeDeValidation[] {
    const e = objet(erreur)
    const liste = objet(e?.details)?.validation_errors ?? objet(e?.problem)?.detail
    return Array.isArray(liste) ? (liste.filter((x) => objet(x) !== null) as EntreeDeValidation[]) : []
}

/**
 * La phrase d'un refus transverse, ou `null` si le refus n'en est pas un — la
 * fonction du module retombe alors sur la phrase du serveur. ⚠️ Un écran qui, sur
 * `null`, rend la main à `toastApiError` écarte d'abord l'incident
 * (`estUnIncident(e) ? null : phraseDeRefusTransverse(e, t)`) : sinon la panne s'affiche
 * en toast court, sans la référence persistante ni « Copier » que le support demande.
 */
export function phraseDeRefusTransverse(erreur: unknown, t: TFunction): string | null {
    // `fetch` rejette en `TypeError` quand la requête n'atteint pas le serveur.
    if (erreur instanceof TypeError) return t('common.networkError')
    if (estUnIncident(erreur)) return phraseDIncident(t, referenceDe(erreur))
    const entrees = entreesDeValidation(erreur)
    if (entrees.length > 0) return direLesEntrees(entrees, (entree) => phraseDEntreeDeValidation(entree, t))
    const code = codeDuRefusTransverse(erreur)
    switch (code) {
        case 'INSUFFICIENT_PERMISSION':
            return t(
                'common.refusTransverses.permission',
                'Votre profil n’a pas le droit nécessaire pour cette action. Demandez-le à un administrateur de votre organisme.',
            )
        // Les deux gardes « Super Admin » (`api/admin/users_helpers.py`) : la phrase du droit manquant
        // y était FAUSSE — « demandez-le à un administrateur » à l'administrateur qui agit sur un
        // Super Admin. `INSUFFICIENT_PRIVILEGE` : la cible est Super Admin, l'acteur ne l'est pas
        // (fiche d'un collaborateur, double authentification) ; `ADMIN_REQUIRED` : la route elle-même
        // (`_require_super_admin`, catalogue central). ⚠️ `api/audit.py` émet aussi `ADMIN_REQUIRED`,
        // pour une requête SANS utilisateur, que l'authentification refuse en amont.
        case 'INSUFFICIENT_PRIVILEGE':
            return t(
                'common.refusTransverses.superAdminSurSuperAdmin',
                'Seul un Super Admin peut agir sur le compte d’un autre Super Admin. Demandez-le à un Super Admin de votre organisme.',
            )
        case 'ADMIN_REQUIRED':
            return t(
                'common.refusTransverses.superAdmin',
                'Cette action est réservée au profil Super Admin. Demandez-la à un Super Admin de votre organisme.',
            )
        case 'FEATURE_NOT_AVAILABLE':
            return t(
                'common.refusTransverses.formule',
                'Cette fonctionnalité n’est pas incluse dans la formule de votre organisme.',
            )
        // (`MISSING_ENTITY_ID` retiré le 2026-09-24 : aucun émetteur côté serveur, une
        // traduction morte laissait croire à une couverture.)
        case 'ENTITY_ID_REQUIRED':
            return t(
                'common.refusTransverses.entitePasPrete',
                'Les informations de votre organisme ne sont pas encore prêtes. Patientez une seconde puis réessayez.',
            )
        // `ENTITY_ACCESS_DENIED` : la garde d'entité des réglages (`settings_helpers.py`),
        // en anglais (« You don't have access to this entity ») — même sortie.
        // `ENTITY_OUT_OF_SCOPE` (« Entité hors de votre périmètre. ») — émetteurs : les
        // lignes de critères de veille (via `messageDeRefus`) ; l'export de formations
        // en lot (`BulkExportDialog`) et celui des vCards d'apprenants
        // (`refusDeLExportVcard`) — tous consultent cette table AVANT `toastApiError`.
        case 'ENTITY_NOT_ALLOWED':
        case 'ENTITY_ACCESS_DENIED':
        case 'ENTITY_OUT_OF_SCOPE':
            return t(
                'common.refusTransverses.entiteHorsPerimetre',
                'Cette entité n’est pas dans votre périmètre : choisissez-en une autre dans le sélecteur d’entité.',
            )
        // A49 : l'administration des utilisateurs est bornée aux entités de l'acteur
        // (`api/admin/_users_perimetre.py`) — gardes partagées des routes `/admin/users*` et de
        // la réinitialisation de la double authentification ; phrase française côté serveur.
        case 'USER_ROLE_ENTITY_OUT_OF_SCOPE':
            return t(
                'common.refusTransverses.roleHorsPerimetre',
                'Vous ne pouvez donner, retirer ou changer un accès que sur les entités où vous gérez les utilisateurs. Pour une autre entité, demandez-le à un Super Admin ou à un administrateur de cette entité.',
            )
        case 'USER_OUT_OF_SCOPE':
            return t(
                'common.refusTransverses.compteHorsPerimetre',
                'Ce collaborateur a un accès sur une entité où vous ne gérez pas les utilisateurs : seul un Super Admin ou un administrateur de cette entité peut agir sur son compte.',
            )
        case 'UNAUTHORIZED':
            return t('common.refusTransverses.sessionExpiree', 'Votre session a expiré : reconnectez-vous.')
        // `PORTAL_ACCOUNT_ONLY` : `session_principal.refuser_un_compte_portal`, garde PARTAGÉE de toute
        // route hors de `/api/portal/`, phrase ANGLAISE quelle que soit la langue de l'écran. Le cookie
        // du back-office et celui de l'espace sont le MÊME : une administratrice qui entre dans
        // l'espace de son apprenant depuis un autre onglet (« Se déconnecter et continuer ») remplace
        // la session de l'onglet du back-office resté ouvert, que `AuthContext` ne revérifie qu'au
        // montage. Recharger la page la conduit à l'espace (`AuthContext`), d'où elle se déconnecte.
        case 'PORTAL_ACCOUNT_ONLY':
            return t(
                'common.refusTransverses.sessionEspace',
                'Ce navigateur est maintenant connecté à un espace externe (apprenant, client ou intervenant), qui n’a pas accès au back-office. Rechargez la page pour ouvrir cet espace, déconnectez-vous, puis reconnectez-vous avec votre compte du back-office.',
            )
        // Le plafond est DIT quand le serveur le joint (`maximum_mo`) : la phrase du serveur le
        // nommait (« (32 Mo) »), la table ne doit pas le retirer à l'écran français.
        case REFUS_SAISIE_TROP_LOURDE: {
            const maximum = plafondEnMo(erreur)
            return maximum === null
                ? t(
                      'common.refusTransverses.saisieTropLourde',
                      'Les données envoyées sont trop volumineuses pour être enregistrées : allégez le contenu saisi ou collé, puis réessayez.',
                  )
                : t('common.refusTransverses.saisieTropLourdeAuDelaDe', {
                      maximum,
                      defaultValue:
                          'Les données envoyées dépassent la taille maximale d’une saisie ({{maximum}} Mo) : allégez le contenu saisi ou collé, puis réessayez.',
                  })
        }
    }
    return phraseDeFamille(code, t)
}

/** Le plafond (en Mo) que le 413 de la borne du corps joint, lu par FORME (`details` d'une
 *  `ApiError`, `problem.detail` d'une erreur des appels d'offres) ; `null` s'il n'est pas lisible. */
function plafondEnMo(erreur: unknown): number | null {
    const e = objet(erreur)
    const maximum = objet(e?.details)?.maximum_mo ?? objet(objet(e?.problem)?.detail)?.maximum_mo
    return typeof maximum === 'number' && Number.isInteger(maximum) && maximum > 0 ? maximum : null
}

/**
 * La phrase d'une FAMILLE (un suffixe, pas un code) : GÉNÉRIQUE, quand le module en rédige une
 * précise (« Cette réservation est introuvable. ») — `toastApiError` laisse celle-ci à
 * l'interface française. Un 404 « muet » (anti-énumération) ne dit ni si l'objet existe
 * ailleurs, ni pourquoi : la seule sortie vraie est de recharger.
 */
export function phraseDeFamille(code: string | null, t: TFunction): string | null {
    if (code !== 'NOT_FOUND' && !code?.endsWith('_NOT_FOUND')) return null
    return t(
        'common.refusTransverses.introuvable',
        'Cet élément n’existe plus, ou n’est pas accessible depuis cette entité. Rechargez la page.',
    )
}
